import {
  BrandMetricsSchema,
  CampaignImpactSchema,
  CampaignStatsSchema,
  CatalogSearchSchema,
  CampaignUpcsSchema,
  PlatformStatsSchema,
  ProductDemandSchema,
  ProductFiltersSchema,
  ProductRawSchema,
  RetailerCategoriesSchema,
  RetailerOverviewSchema,
  RetailerProductsSchema,
  RetailerRedemptionsSchema,
  RetailerTrafficSchema,
  ShopperGrowthSchema,
  UpcMetricsSchema,
  UserPhoneSchema,
  type BrandMetrics,
  type CampaignImpact,
  type CampaignStats,
  type CatalogSearch,
  type CampaignUpcs,
  type PlatformStats,
  type ProductDemand,
  type ProductFilters,
  type ProductRaw,
  type RetailerCategories,
  type RetailerOverview,
  type RetailerProducts,
  type RetailerRedemptions,
  type RetailerTraffic,
  type ShopperGrowth,
  type UpcMetrics,
  type UserPhone,
} from "./types";
import {
  mockBrandMetrics,
  mockCampaignImpact,
  mockCampaignStats,
  mockCatalogSearch,
  mockCampaignUpcs,
  mockPlatformStats,
  mockProductDemand,
  mockProductFilters,
  mockProductRaw,
  mockRetailerCategories,
  mockRetailerOverview,
  mockRetailerProducts,
  mockRetailerRedemptions,
  mockRetailerTraffic,
  mockShopperGrowth,
  mockUpcMetrics,
  mockUserPhone,
} from "./mock";

import { appConfig } from "@/config";

const USE_MOCKS = import.meta.env.VITE_API_MOCK === "true";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function authedFetch(path: string, token: string): Promise<unknown> {
  const res = await fetch(`${appConfig.apiBaseUrl}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new ApiError(`API ${path} failed: ${res.status}`, res.status);
  }
  return res.json();
}

async function mockDelay<T>(value: T): Promise<T> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return value;
}

export async function getPlatformStats(token: string): Promise<PlatformStats> {
  if (USE_MOCKS) return mockDelay(mockPlatformStats());
  return PlatformStatsSchema.parse(
    await authedFetch("/api/v1/ops/platform-stats", token),
  );
}

export async function getUserPhone(
  token: string,
  userId: string,
): Promise<UserPhone> {
  if (USE_MOCKS) return mockDelay(mockUserPhone(userId));
  return UserPhoneSchema.parse(
    await authedFetch(
      `/api/v1/ops/users/${encodeURIComponent(userId)}/phone`,
      token,
    ),
  );
}

export async function getCampaignStats(token: string): Promise<CampaignStats> {
  if (USE_MOCKS) return mockDelay(mockCampaignStats());
  return CampaignStatsSchema.parse(
    await authedFetch("/api/v1/ops/campaign-stats", token),
  );
}

export async function getCampaignUpcs(token: string): Promise<CampaignUpcs> {
  if (USE_MOCKS) return mockDelay(mockCampaignUpcs());
  return CampaignUpcsSchema.parse(
    await authedFetch("/api/v1/ops/campaign-upcs", token),
  );
}

function queryString(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value) search.set(name, value);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// --- Retailer insights ---

// Type aliases (not interfaces) so they satisfy queryString's Record param.
export type RetailerQuery = {
  storeId?: string;
  subcategoryId?: string; // level-2
};

export async function getRetailerRedemptions(
  token: string,
  query: RetailerQuery = {},
): Promise<RetailerRedemptions> {
  if (USE_MOCKS) return mockDelay(mockRetailerRedemptions(query.storeId));
  return RetailerRedemptionsSchema.parse(
    await authedFetch(
      `/api/v1/retailer/redemptions${queryString(query)}`,
      token,
    ),
  );
}

export async function getRetailerOverview(
  token: string,
  query: RetailerQuery = {},
): Promise<RetailerOverview> {
  if (USE_MOCKS)
    return mockDelay(mockRetailerOverview(query.storeId, query.subcategoryId));
  return RetailerOverviewSchema.parse(
    await authedFetch(
      `/api/v1/retailer/overview${queryString(query)}`,
      token,
    ),
  );
}

export async function getRetailerTraffic(
  token: string,
  query: RetailerQuery = {},
): Promise<RetailerTraffic> {
  if (USE_MOCKS)
    return mockDelay(mockRetailerTraffic(query.storeId, query.subcategoryId));
  return RetailerTrafficSchema.parse(
    await authedFetch(
      `/api/v1/retailer/traffic${queryString(query)}`,
      token,
    ),
  );
}

export async function getRetailerCategories(
  token: string,
  query: RetailerQuery = {},
): Promise<RetailerCategories> {
  if (USE_MOCKS)
    return mockDelay(mockRetailerCategories(query.storeId, query.subcategoryId));
  return RetailerCategoriesSchema.parse(
    await authedFetch(
      `/api/v1/retailer/categories${queryString(query)}`,
      token,
    ),
  );
}

export async function getRetailerProducts(
  token: string,
  query: RetailerQuery = {},
): Promise<RetailerProducts> {
  if (USE_MOCKS)
    return mockDelay(mockRetailerProducts(query.storeId, query.subcategoryId));
  return RetailerProductsSchema.parse(
    await authedFetch(
      `/api/v1/retailer/products${queryString(query)}`,
      token,
    ),
  );
}

export async function getShopperGrowth(
  token: string,
  query: { storeId?: string } = {},
): Promise<ShopperGrowth> {
  if (USE_MOCKS) return mockDelay(mockShopperGrowth(query.storeId));
  return ShopperGrowthSchema.parse(
    await authedFetch(`/api/v1/retailer/shopper-growth${queryString(query)}`, token),
  );
}

// --- Product performance insights ---

export async function getProductFilters(
  token: string,
): Promise<ProductFilters> {
  if (USE_MOCKS) return mockDelay(mockProductFilters());
  return ProductFiltersSchema.parse(
    await authedFetch("/api/v1/product/filters", token),
  );
}

/** Resolves to null when no campaigns are given — the endpoint requires them. */
export async function getCampaignImpact(
  token: string,
  query: { campaignId?: string; upcs?: string } = {},
): Promise<CampaignImpact | null> {
  if (!query.campaignId?.trim()) return null;
  if (USE_MOCKS)
    return mockDelay(mockCampaignImpact(query.campaignId, query.upcs));
  return CampaignImpactSchema.parse(
    await authedFetch(
      `/api/v1/product/campaign-impact${queryString(query)}`,
      token,
    ),
  );
}

export async function getBrandMetrics(
  token: string,
  query: { brandId?: string; subcategoryId?: string } = {},
): Promise<BrandMetrics> {
  if (USE_MOCKS)
    return mockDelay(mockBrandMetrics(query.brandId, query.subcategoryId));
  return BrandMetricsSchema.parse(
    await authedFetch(`/api/v1/product/brand-metrics${queryString(query)}`, token),
  );
}

/** Resolves to null when no UPCs are given — the endpoint requires them. */
export async function getUpcMetrics(
  token: string,
  query: { upcs?: string; dma?: string; storeId?: string } = {},
): Promise<UpcMetrics | null> {
  if (!query.upcs?.trim()) return null;
  if (USE_MOCKS)
    return mockDelay(mockUpcMetrics(query.upcs, query.dma, query.storeId));
  return UpcMetricsSchema.parse(
    await authedFetch(`/api/v1/product/upc-metrics${queryString(query)}`, token),
  );
}

export async function getProductRaw(
  token: string,
  query: {
    storeId?: string;
    brandId?: string;
    subcategoryId?: string; // level-2
    dma?: string;
    upcs?: string;
  } = {},
): Promise<ProductRaw> {
  if (USE_MOCKS) return mockDelay(mockProductRaw(query));
  return ProductRawSchema.parse(
    await authedFetch(`/api/v1/product/raw${queryString(query)}`, token),
  );
}

export async function getProductDemand(
  token: string,
  query: {
    q?: string;
    storeId?: string;
    brandId?: string;
    categoryId?: string; // level-1
    subcategoryId?: string; // level-2
  } = {},
): Promise<ProductDemand> {
  if (USE_MOCKS) return mockDelay(mockProductDemand(query.q, query));
  return ProductDemandSchema.parse(
    await authedFetch(`/api/v1/product/demand${queryString(query)}`, token),
  );
}

export async function getCatalogSearch(
  token: string,
  query: {
    q?: string;
    brandId?: string;
    categoryId?: string; // level-1
    subcategoryId?: string; // level-2
  } = {},
): Promise<CatalogSearch> {
  if (USE_MOCKS) return mockDelay(mockCatalogSearch(query.q));
  return CatalogSearchSchema.parse(
    await authedFetch(`/api/v1/product/catalog-search${queryString(query)}`, token),
  );
}
